import * as React from 'react';
// eslint-disable-next-line no-unused-vars
import { EventFormat } from '../../../../common/types/EventFormat';
import { Button, Grid, TextField, makeStyles } from '@material-ui/core';
import { Colors } from '../../../../styles/Colors';
import DragAndDropHelper from '../../../../common/helpers/dragAndDropHelper';
import { bountyRoundCount } from '../../../../common/helpers/eventFormatHelper';


const byName = (items) =>
    [...items].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));

const row = {
    borderRadius: 8,
    padding: 8,
    margin: 4,
    width: 'fit-content',
    minWidth: 220,
    maxWidth: 380
};

const useStyles = makeStyles((theme) => ({
    draggable: {
        ...row,
        background: Colors.panel,
        cursor: 'grab'
    },
    droppable: {
        ...row,
        background: Colors.panelLight,
        color: Colors.panelLighterColor,
        cursor: 'not-allowed'
    },
    droppableDefined: {
        ...row,
        background: Colors.panel,
        color: Colors.pickstop,
        cursor: 'not-allowed'
    },
    subDrop: {
        marginLeft: 12
    },
    createButton: {
        marginLeft: 8,
        '&.Mui-disabled': {
            background: Colors.panelLight,
            color: Colors.primaryDarker
        }
    },
    instructions: {
        opacity: 0.8,
        marginBottom: 8
    }
}));

/**
 * @typedef {Object} EventMapperProps
 * @property {any} details
 * @property {EventFormat} format
 */

/**
 * 
 * @param {EventMapperProps} param0
 * @returns
 */
export default function EventMapper({ details, format, onCreate }) {
    const classes = useStyles();
    let stageData = JSON.parse(format.json);
    const [state, setState] = React.useState({ stages: {} });

    React.useEffect(() => {
        if (Object.keys(state?.stages ?? {}).length !== stageData.stages.length) {
            let newState = {
                stages: {

                }
            };

            for (let i = 0; i < stageData.stages.length; i++) {
                newState.stages[i] = {
                    matchMappingKeys: [],
                    standingsMappingKeys: [],
                    substageTitles: []
                }
            }

            setState(newState);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details, format]);

    const onDragStart = (isMatch, stage, group) => {
        DragAndDropHelper.onDragStart({
            isMatch,
            stage,
            group
        });
    }

    const addToMapping = (list, key, index) => {
        while (list.length < index + 1) {
            list.push(null);
        }

        list[index] = key;
    }

    const onDrop = (stageIndex, substageIndex) => {
        const { isMatch, stage, group } = DragAndDropHelper.getDragData();

        const formatStage = stageData.stages[stageIndex];
        const targetMapping = state.stages[stageIndex];

        const matchMapping = {
            id: group.key,
            displayName: group.name,
            liquipediaPageId: stage.id,
            liquipediaPage: stage.name
        };
        const standingsMapping = {
            id: group.id,
            displayName: group.name,
            liquipediaPageId: stage.id,
            liquipediaPage: stage.name
        };

        // Group Stage
        if (formatStage.type === 1) {
            if (formatStage.groupStageDetails?.hasGroupBrackets) {
                if (isMatch) {
                    addToMapping(targetMapping.matchMappingKeys, matchMapping, substageIndex)
                    if (!targetMapping.substageTitles[substageIndex]) {
                        targetMapping.substageTitles[substageIndex] = group.name;
                    }
                }
            }
            else {
                if (isMatch) {
                    addToMapping(targetMapping.matchMappingKeys, matchMapping, substageIndex)
                }
                else {
                    addToMapping(targetMapping.standingsMappingKeys, standingsMapping, substageIndex)
                }
            }
        }
        // Swiss Stage
        else if (formatStage.type === 2) {
            if (isMatch) {
                addToMapping(targetMapping.matchMappingKeys, matchMapping, substageIndex)
            }
            else {
                addToMapping(targetMapping.standingsMappingKeys, standingsMapping, substageIndex)
            }
        }
        else {
            if (isMatch) {
                addToMapping(targetMapping.matchMappingKeys, matchMapping, substageIndex)
            }
        }

        setState({...state});
    }

    const onReset = () => {
        let newState = {
            stages: {

            }
        };

        for (let i = 0; i < stageData.stages.length; i++) {
            newState.stages[i] = {
                matchMappingKeys: [],
                standingsMappingKeys: [],
                substageTitles: []
            }
        }

        setState(newState);
    }

    let disableCreateEvent = false;
    const stageTypeName = (type) => (type === 1 ? 'Group' : type === 2 ? 'Swiss' : type === 4 ? 'Bounty' : 'Playoffs');
    const eventStagesMapped = stageData.stages.map((stage, i) => {
        const subStages = state?.stages[i];
        let subStageCount = 1;

        // Group Stage
        if (stage.type === 1) {
            subStageCount = stage.groupStageDetails?.groupCount ?? 1;
        }
        // Swiss or Playoffs
        else if (stage.type === 2 || stage.type === 3) {
            subStageCount = 1;
        }
        // Bounty: one ordered bracket per round.
        else if (stage.type === 4) {
            subStageCount = bountyRoundCount(stage.bountyStageDetails?.teamCount);
        }

        const stageElements = [];
        for (let j = 0; j < subStageCount; j++) {
            let defined = !!(subStages?.standingsMappingKeys[j]?.displayName ?? subStages?.matchMappingKeys[j]?.displayName);
            const isMatch = !((stage.type === 1 && !stage.groupStageDetails?.hasGroupBrackets) || stage.type === 2);
            const emptyLabel = stage.type === 4
                ? `Drop Round ${j + 1} Bracket here`
                : isMatch ? 'Drop a Bracket here' : 'Drop Standings here';
            let stageDisplayName = subStages?.standingsMappingKeys[j]?.displayName ?? subStages?.matchMappingKeys[j]?.displayName ?? emptyLabel;
            stageElements.push(
                <div className={defined ? classes.droppableDefined : classes.droppable}
                    onDragOver={(e) => { e.preventDefault() }}
                    onDrop={() => onDrop(i, j)}>
                    {stageDisplayName}
                </div>)

            if (!defined) {
                disableCreateEvent = true;
            }

            // Sets EventSubstage.Title, blank keeps the raw section name.
            if (stage.type === 1 && stage.groupStageDetails?.hasGroupBrackets && defined) {
                stageElements.push(
                    <TextField
                        key={`title-${i}-${j}`}
                        className={classes.subDrop}
                        size="small"
                        placeholder="Substage title"
                        value={subStages?.substageTitles[j] ?? ''}
                        onChange={(e) => {
                            subStages.substageTitles[j] = e.target.value;
                            setState({ ...state });
                        }}
                    />)
            }

            // Need to specify match for the standing table
            if ((stage.type === 1 && !stage.groupStageDetails?.hasGroupBrackets) || stage.type === 2) {
                defined = !!(subStages?.matchMappingKeys[j]?.displayName);
                stageElements.push(
                    <div className={(defined ? classes.droppableDefined : classes.droppable) + ' ' + classes.subDrop}
                        onDragOver={(e) => { e.preventDefault() }}
                        onDrop={() => onDrop(i, j)}>
                        {subStages?.matchMappingKeys[j]?.displayName ?? 'Drop a Bracket here to set the start time'}
                    </div>)

                if (!defined) {
                    disableCreateEvent = true;
                }
            }
        }

        return <div>
            <label>{stage.name} ({stageTypeName(stage.type)})</label>
            {stageElements}
        </div>
    });

    return <>
        <p className={classes.instructions}>
            Drag an API Stage (right) onto the matching format slot (left). Every slot must be filled.
        </p>
        <Grid container>
            <Grid item xs={5}>
                <h1>Event Stages</h1>
                {
                    eventStagesMapped
                }
                <div></div>
            </Grid>
            <Grid item xs={7}><h1>API Stages</h1>
                {
                    byName(details.stageDetails).map((stage) => {
                        return <div key={stage.id}>
                            <label><a target="_blank" href={stage.link} rel="noopener noreferrer">{stage.name}</a></label>
                            {
                                byName(stage.standingGroups).map((group) => {
                                    return <div key={`standings-${group.key}`} className={classes.draggable} draggable="true" onDragStart={() => onDragStart(false, stage, group)}>{group.name} - Standings</div>
                                })
                            }
                            {
                                byName(stage.matchGroups).map((group) => {
                                    return <div key={`bracket-${group.key}`} className={classes.draggable} draggable="true" onDragStart={() => onDragStart(true, stage, group)}>{group.name} - Bracket</div>
                                })
                            }
                        </div>
                    })
                }
            </Grid>
        </Grid>
        <Button color="primary" onClick={onReset}>Reset Mappings</Button>
        <Button
            className={classes.createButton}
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => { onCreate(state); }}
            disabled={disableCreateEvent}
        >
            Create Event
        </Button>
    </>
}